import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { api } from '@/lib/api'
import { SongRowContent, type Song } from './song-row-content'

export function SortableSongRow({
	song,
	index,
	playlistId,
	accentColor,
	isCurrent,
	disabled,
	onPlay,
	onMoveToTop,
}: {
	song: Song
	index: number
	playlistId: string
	accentColor: string
	isCurrent: boolean
	disabled?: boolean
	onPlay: () => void
	onMoveToTop: () => void
}) {
	const queryClientHook = useQueryClient()
	const {
		attributes,
		listeners,
		setNodeRef,
		transform,
		isDragging,
	} = useSortable({ id: song.id, disabled, animateLayoutChanges: () => false })

	const removeMutation = useMutation({
		mutationFn: async () => {
			const response = await api.api.songs({ id: song.id }).delete()
			if (response.error) {
				throw new Error('Failed to remove song')
			}
			return response.data
		},
		onSuccess: () => {
			queryClientHook.invalidateQueries({ queryKey: ['songs', { playlistId }] })
		},
	})

	const style = {
		transform: CSS.Translate.toString(transform),
	}

	return (
		<div ref={setNodeRef} style={style} {...attributes}>
			<SongRowContent
				song={song}
				index={index}
				accentColor={accentColor}
				isPlaceholder={isDragging}
				isCurrent={isCurrent}
				dragHandleProps={disabled ? undefined : listeners}
				onPlay={onPlay}
				onRemove={() => removeMutation.mutate()}
				isRemoving={removeMutation.isPending}
				onMoveToTop={disabled ? undefined : onMoveToTop}
			/>
		</div>
	)
}
